import apiClient from './apiClient';
import { ReplyPostData, PostProps, PostMedia } from '../types/postType';

export const createPost = async (postData: PostProps): Promise<void> => {
  const formData = new FormData();
  formData.append('text', postData.content);
  
  // Handle media files
  if (postData.mediaList && postData.mediaList.length > 0) {
    const mediaOrder: string[] = [];
    
    postData.mediaList.forEach((media, index) => {
      if (media.file) {
        formData.append(`media_${index}`, media.file);
        mediaOrder.push(`media_${index}`);
      } else if (media.media_type === 'gif') {
        mediaOrder.push(media.url); // For GIFs, push the URL directly
      }
    });

    if (mediaOrder.length > 0) {
      formData.append('media_order', JSON.stringify(mediaOrder));
    }
  }

  // Handle highlights
  if (postData.highlights && postData.highlights.length > 0) {
    const highlightsData = postData.highlights.map((highlight, index) => ({
      highlight_text: highlight.highlight_text,
      highlight_type: highlight.highlight_type,
      mention_type: highlight.mention_type,
      position: index
    }));
    formData.append('highlights', JSON.stringify(highlightsData));
  }

  const accessToken = localStorage.getItem('accessToken');
  
  try {
    const response = await apiClient.post('/core/post/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${accessToken}`
      },
    });

    if (response.data.msg !== "Post created successfully") {
      throw new Error('Failed to create post');
    }
  } catch (error) {
    console.error('Error creating post:', error);
    throw error;
  }
};

export interface ApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: PostProps[];
}

export const fetchPosts = async (page: number = 1, pageSize: number = 100): Promise<ApiResponse> => {
  try {
    const accessToken = localStorage.getItem('accessToken'); // Assuming you store the token in localStorage
    console.log(accessToken);
    const response = await apiClient.get<ApiResponse>('/core/post/', {
      params: {
        page,
        page_size: pageSize
      },
      headers: {
        'Authorization': `Bearer ${accessToken}` // Add this line to include the token
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching posts:', error);
    throw error;
  }
};

interface LikeResponse {
  success: boolean;
  likedStatus: boolean;
  likeCount: number;
}

export const likePost = async (postId: string): Promise<LikeResponse> => {
  try {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.post<LikeResponse>(`/core/post/${postId}/like/`, null, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error liking post:', error);
    throw error;
  }
};

export const deletePost = async (postId: number): Promise<boolean> => {
  try {
    const accessToken = localStorage.getItem('accessToken');
    await apiClient.delete(`/core/post/${postId}/delete/`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    return true;
  } catch (error) {
    console.error('Error deleting post:', error);
    return false;
  }
};

export const createReplyPost = async (replyData: ReplyPostData): Promise<void> => {
  const formData = new FormData();
  formData.append('text', replyData.content);
  formData.append('parent_post_id', replyData.parentPostId.toString());
  
  // Add the direct parent ID for nested replies
  if (replyData.parent_id && replyData.parent_id !== replyData.parentPostId) {
    formData.append('parent_id', replyData.parent_id.toString());
  }

  // Handle media files
  if (replyData.media && replyData.media.length > 0) {
    const mediaOrder: string[] = [];
    
    replyData.media.forEach((media, index) => {
      if (media.file) {
        formData.append(`media_${index}`, media.file);
        mediaOrder.push(`media_${index}`);
      } else if (media.media_type === 'gif') {
        mediaOrder.push(media.url);
      }
    });

    if (mediaOrder.length > 0) {
      formData.append('media_order', JSON.stringify(mediaOrder));
    }
  }

  try {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.post('/core/post/reply/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${accessToken}`
      },
    });

    if (response.status !== 201 && response.status !== 200) {
      throw new Error('Failed to create reply post');
    }
  } catch (error) {
    console.error('Error creating reply post:', error);
    throw error;
  }
};

interface ApiPostDetailsResponse {
  msg: string;
  data: {
    post: PostProps;
  };
  code: null;
  log: null;
}

export const fetchPostDetails = async (postSlug: string): Promise<PostProps> => {
  try {
    const response = await apiClient.get(`/core/post/${postSlug}/`);
    
    // Check if response is in the new format (with results array)
    if (response.data.results && Array.isArray(response.data.results)) {
      // Find the main post (usually the first item in results)
      const mainPost = response.data.results.find((post: any) => post.slug === postSlug);
      if (!mainPost) {
        throw new Error('Post not found in results');
      }
      return mainPost;
    } 
    // Fall back to old format if needed
    else if (response.data.data && response.data.data.post) {
      return response.data.data.post;
    } 
    else {
      throw new Error('Unexpected API response format');
    }
  } catch (error) {
    console.error('Error fetching post details:', error);
    throw error;
  }
};

interface ApiPostRepliesResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: PostProps[];
}

export const fetchPostReplies = async (
  postSlug: string, 
  page: number = 1, 
  pageSize: number = 100 
): Promise<ApiPostRepliesResponse> => {
  try {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.get<ApiPostRepliesResponse>(
      `/core/post/${postSlug}/replies/`,
      {
        params: {
          page,
          page_size: pageSize
        },
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching post replies:', error);
    throw error;
  }
};
